import React, { useState, useEffect } from 'react';
import { useLabs, useHints, useLabCompletion } from '../../hooks/useLabs';
import './Labs.css';
import { SimTerminal } from './SimTerminal';

export const LevelView = ({ routeId, levelId }) => {
  const { routes, userProgress, selectedRoute, setSelectedLevel } = useLabs();
  const { completeLevel } = useLabCompletion();
  const { getHint } = useHints();

  const [level, setLevel] = useState(null);
  const [flag, setFlag] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showUnlockNotification, setShowUnlockNotification] = useState(false);
  const [unlockedLevelInfo, setUnlockedLevelInfo] = useState(null);
  const [hintsUsed, setHintsUsed] = useState([]);
  const [currentHint, setCurrentHint] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [terminalFlagReceived, setTerminalFlagReceived] = useState(null);

  useEffect(() => {
    const route = routes.find(r => r.id === routeId);
    if (route) {
      const foundLevel = route.niveles?.find(l => l.id === levelId);
      setLevel(foundLevel);
    }
  }, [routeId, levelId, routes]);

  // Resetear estados cuando cambia el nivel
  useEffect(() => {
    setFlag('');
    setMessage('');
    setTerminalFlagReceived(null);
    setFailedAttempts(0);
    setShowAnswer(false);
    setCurrentHint(null);
    setHintsUsed([]);
  }, [routeId, levelId]);

  const handleSubmitFlag = async (e) => {
    e.preventDefault();
    if (!flag.trim()) {
      setMessage('⚠️ Ingresa una flag');
      return;
    }

    setLoading(true);
    try {
      console.log('Enviando flag:', JSON.stringify(flag));
      console.log('Flag esperada:', JSON.stringify(level.flag_esperada));
      const result = await completeLevel(routeId, levelId, flag);
      console.log('Resultado del backend:', result);
      
      if (result.success) {
        const successMsg = result.isNewCompletion 
          ? `¡Correcto! +${result.xpGained} XP`
          : `¡Correcto! (Ya completado anteriormente)`;
        
        setMessage(successMsg);
        setFlag('');
        setFailedAttempts(0);
        
        // Si hay un siguiente nivel desbloqueado, mostrar notificación especial
        if (result.nextLevel && result.isNewCompletion) {
          setUnlockedLevelInfo(result.nextLevel);
          setShowUnlockNotification(true);
          
          // Espera 3 segundos, luego navega al siguiente nivel
          await new Promise(resolve => setTimeout(resolve, 3000));
          setShowUnlockNotification(false);
          setSelectedLevel(result.nextLevel.id);
          setMessage('');
        } else {
          // Si no hay siguiente nivel o no es nuevo, esperar 2 segundos y volver
          await new Promise(resolve => setTimeout(resolve, 2000));
          setSelectedLevel(null);
        }
      } else {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        if (newAttempts >= 5) {
          setShowAnswer(true);
          setMessage('Muchos intentos fallidos. La respuesta ha sido revelada.');
        } else {
          setMessage(`Flag incorrecto. Intenta de nuevo. (${newAttempts}/5 intentos)`);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setMessage('Error al procesar la flag. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Terminal simulado: activar para rutas de Bash/Kali con pasos guiados
  const routeInfo = routes.find(r => r.id === routeId);
  const requiresTerminal = Boolean(
    (routeId === 'bash-shell' || routeId === 'kali-linux') && (level?.pasos_guiados?.length)
  );

  const handleGetHint = async (index) => {
    if (hintsUsed.includes(index)) {
      setCurrentHint(level.pistas[index].contenido);
      return;
    }

    const hint = await getHint(routeId, levelId, index);
    if (hint) {
      setCurrentHint(hint.contenido);
      setHintsUsed([...hintsUsed, index]);
    }
  };

  if (!level) {
    return <div className="level-view loading">Cargando nivel...</div>;
  }

  const isCompleted = userProgress[routeId]?.niveles?.find(
    l => l.id === levelId
  )?.completado;

  // Dificultad puede no estar definida a nivel de 'level'; usa la de la ruta como respaldo
  const difficulty = (level?.dificultad || routeInfo?.dificultad || 'Intermedio');

  return (
    <div className="level-view">
      {/* Botón para toggle sidebar en móvil */}
      <button 
        className="sidebar-toggle"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? '◀' : '▶'}
      </button>

      {/* Panel izquierdo: Información del nivel */}
      <div className={`level-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>{level.titulo}</h2>
          <button 
            className="close-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsSidebarOpen(false);
            }}
            title="Cerrar panel lateral"
          >
            ✕
          </button>
        </div>

        <div className="sidebar-section">
          <h3>📖 Escenario</h3>
          <p>{level.escenario}</p>
        </div>

        <div className="sidebar-section">
          <h3>🎯 Objetivos</h3>
          <ul>
            {level.objetivos?.map((obj, i) => (
              <li key={i}>{obj}</li>
            ))}
          </ul>
        </div>

        <div className="sidebar-section">
          <h3>📋 Pasos Guiados</h3>
          <ol className="guided-steps">
            {level.pasos_guiados?.map((paso, i) => (
              <li key={i} className="step">
                <strong>{paso.titulo}</strong>
                <p>{paso.descripcion}</p>
                {paso.comando && (
                  <code className="command-box">{paso.comando}</code>
                )}
              </li>
            ))}
          </ol>
        </div>

        <div className="sidebar-section">
          <h3>💡 Pistas</h3>
          <div className="hints-container">
            {level.pistas?.map((pista, i) => (
              <div key={i} className="hint-item">
                <button
                  className={`hint-btn ${hintsUsed.includes(i) ? 'used' : ''}`}
                  onClick={() => handleGetHint(i)}
                >
                  Pista {i + 1} {hintsUsed.includes(i) ? '✓' : '?'}
                  {pista.penalizacion_xp > 0 && <span className="xp-penalty"> -{pista.penalizacion_xp} XP</span>}
                </button>
                {hintsUsed.includes(i) && (
                  <div className="hint-display">
                    <p>{pista.contenido}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="sidebar-section">
          <h3>🏆 Competencias</h3>
          <div className="competencies">
            {level.competencias_aprendidas?.map((comp, i) => (
              <span key={i} className="competency-tag">{comp}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Panel derecho: Laboratorio */}
      <div className="level-main">
        {requiresTerminal && (
          <div className="terminal-block">
            <h3>🖥️ Terminal (Simulada)</h3>
            <p className="terminal-hint">Escribe los comandos clave para completar los pasos guiados.</p>
            <SimTerminal
              key={`${routeId}-${levelId}`}
              steps={level.pasos_guiados || []}
              expectedFlag={level.flag_esperada}
              onComplete={(flagFromTerminal) => {
                if (flagFromTerminal) {
                  setTerminalFlagReceived(flagFromTerminal);
                  setFlag(flagFromTerminal);
                  setMessage('✅ Terminal completada. Flag lista para enviar.');
                }
              }}
            />
          </div>
        )}
        <div className="lab-container">
          <div className="lab-iframe-wrapper">
            <iframe
              src={level.url_laboratorio}
              title={`Lab ${level.id}`}
              className="lab-iframe"
            ></iframe>
          </div>

          <div className="lab-controls">
            <form onSubmit={handleSubmitFlag}>
              <div className="form-group">
                <label htmlFor="flag-input">Ingresa la flag:</label>
                <input
                  id="flag-input"
                  type="text"
                  placeholder="flag{...}"
                  value={flag}
                  onChange={(e) => setFlag(e.target.value)}
                  disabled={isCompleted}
                  className="flag-input"
                />
              </div>

              {message && (
                <div className={`message ${message.includes('Correcto') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              {showAnswer && (
                <div className="alert alert-warning answer-reveal">
                  <strong>Respuesta correcta:</strong>
                  <code className="answer-code">{level.flag_esperada}</code>
                  <p className="small">Cópiala en el campo de arriba para continuar.</p>
                </div>
              )}

              {!isCompleted && (
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading || !flag.trim()}
                >
                  {loading ? 'Enviando...' : 'Enviar Flag'}
                </button>
              )}

              {isCompleted && (
                <div className="completed-message">
                  ✓ Nivel completado
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="level-info-footer">
          <p>XP por completar: <strong>{Number(level?.xp_otorgado || 0)}</strong></p>
          <p>Dificultad: <span className={`difficulty ${String(difficulty).toLowerCase()}`}>
            {difficulty}
          </span></p>
        </div>
      </div>

      {/* Notificación de nivel desbloqueado */}
      {showUnlockNotification && unlockedLevelInfo && (
        <div className="unlock-notification">
          <div className="unlock-content">
            <div className="unlock-icon">🎉</div>
            <h3>¡Nivel Desbloqueado!</h3>
            <p>{unlockedLevelInfo.titulo}</p>
            <div className="unlock-progress">Cargando siguiente nivel...</div>
          </div>
        </div>
      )}
    </div>
  );
};
